// An organization's stores.
//
// SHARED MASTER DATA, not support configuration. Four modules will read these rows, and
// a store is what answers "which site is this" — which is why this sits behind the admin
// gate rather than on the Customers tab an agent can reach.
//
// EDITED ONE AT A TIME, unlike queue membership or a form's fields, and the difference is
// what a row means. A location carries an account number that is unique across every
// organization and is referenced by `customers.location_id` and `tickets.location_id`, so
// a replace-the-whole-list editor would have to delete rows that history points at. A
// store is a record; membership is a set.
//
// THE TWO COUNTS ARE THE POINT OF THE ROW. `is_active` filters the store pickers, so
// retiring a store takes it out of reach of an agent taking a call from it — and the
// server refuses while contacts or open tickets still point at it. Showing the counts is
// how an admin sees that refusal coming instead of meeting it.

import { useCallback, useEffect, useState } from 'react';

import { apiFetch } from '../../api/client.js';
import { consoleErrorMessage } from '../../pages/admin/descriptor-form.js';

const BLANK = {
  name: '', account_number: '', store_key: '', store_number: '', phone: '', address: '',
  timezone: '', install_date: '', live_date: '', termination_date: '',
  brand_developer: '', install_project_url: '', is_active: true,
};

function StoreForm({ initial, busy, onSave, onCancel, error }) {
  const [draft, setDraft] = useState(initial);
  const set = (key, value) => setDraft((previous) => ({ ...previous, [key]: value }));

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-line-strong bg-sunken p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium text-ink-secondary">Store name</span>
          <input className="field-input mt-1" value={draft.name}
                 onChange={(e) => set('name', e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-ink-secondary">
            Account number (unique across every organization)
          </span>
          <input className="field-input mt-1" value={draft.account_number ?? ''}
                 onChange={(e) => set('account_number', e.target.value)} />
        </label>
        {/* NOT unique, unlike the account number above, and the desk's own data is the
            reason: `store_key` repeats on the three organization pairs that were cloned
            when a franchisee opened a second site, and `store_number` is a number WITHIN
            a brand — Marco's 1025 and MMP 1025 are different stores — so 134 of them
            repeat and one is shared by 58 organizations. Both are searched daily;
            neither is an identity. */}
        <label className="block">
          <span className="text-xs font-medium text-ink-secondary">
            Store key (not unique)
          </span>
          <input className="field-input mt-1" value={draft.store_key ?? ''}
                 onChange={(e) => set('store_key', e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-ink-secondary">
            Store number (unique only within a brand)
          </span>
          <input className="field-input mt-1" value={draft.store_number ?? ''}
                 onChange={(e) => set('store_number', e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-ink-secondary">Store phone</span>
          <input className="field-input mt-1" value={draft.phone ?? ''}
                 onChange={(e) => set('phone', e.target.value)} />
        </label>
        <label className="block">
          {/* A WINDOWS id, not an IANA one. `AT TIME ZONE` rejects "America/Chicago"
              outright, which the agent-schedule screen already learned the hard way, and
              a store's timezone is what a future module will render its hours in. */}
          <span className="text-xs font-medium text-ink-secondary">
            Time zone (Windows id, e.g. Pacific Standard Time)
          </span>
          <input className="field-input mt-1" value={draft.timezone ?? ''}
                 onChange={(e) => set('timezone', e.target.value)} />
        </label>
      </div>
      <label className="block">
        <span className="text-xs font-medium text-ink-secondary">Address</span>
        <textarea className="field-input mt-1" rows={2} value={draft.address ?? ''}
                  onChange={(e) => set('address', e.target.value)} />
      </label>
      {/* The store's lifecycle. Its own block because these answer a different question
          from everything above — "where is this store in its life" rather than "how do I
          reach it" — and because they are filled by the implementation team, not by
          support taking a call. `type="date"` posts YYYY-MM-DD, which is exactly what the
          API's `date` validator takes and what the column stores; no parsing anywhere,
          so no timezone can move a go-live day. */}
      <fieldset className="rounded-lg border border-line p-3">
        <legend className="px-1 text-xs font-medium text-ink-secondary">Lifecycle</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="text-xs font-medium text-ink-secondary">Install date</span>
            <input type="date" className="field-input mt-1" value={draft.install_date ?? ''}
                   onChange={(e) => set('install_date', e.target.value)} />
          </label>
          <label className="block">
            {/* Zendesk titles this "Live/Transfer Date" — it marks both a new store going
                live and an existing one changing hands. */}
            <span className="text-xs font-medium text-ink-secondary">Live / transfer date</span>
            <input type="date" className="field-input mt-1" value={draft.live_date ?? ''}
                   onChange={(e) => set('live_date', e.target.value)} />
          </label>
          <label className="block">
            {/* Zendesk's key for this is `transfer_date__most_recent_`, which is stale and
                says the opposite of what it holds: the field agents see is titled
                "Termination Date". Named here for what it means. */}
            <span className="text-xs font-medium text-ink-secondary">Termination date</span>
            <input type="date" className="field-input mt-1" value={draft.termination_date ?? ''}
                   onChange={(e) => set('termination_date', e.target.value)} />
          </label>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block">
            {/* A closed dropdown of seven in Zendesk, free text here on purpose: the list
                is Zendesk's to change, and a fixed set would refuse the eighth developer
                until somebody redeployed this screen. */}
            <span className="text-xs font-medium text-ink-secondary">
              Brand developer (e.g. mmp_pelican_group)
            </span>
            <input className="field-input mt-1" value={draft.brand_developer ?? ''}
                   onChange={(e) => set('brand_developer', e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-ink-secondary">
              Install project URL (Asana)
            </span>
            <input type="url" className="field-input mt-1" value={draft.install_project_url ?? ''}
                   onChange={(e) => set('install_project_url', e.target.value)} />
          </label>
        </div>
      </fieldset>

      <label className="flex items-center gap-2 text-sm text-ink">
        <input type="checkbox" className="size-4 rounded accent-brand-600"
               checked={draft.is_active !== false}
               onChange={(e) => set('is_active', e.target.checked)} />
        Active — an inactive store cannot be picked on a new ticket
      </label>

      {error && <p className="text-sm text-red-700 dark:text-red-300">{error}</p>}

      <div className="flex items-center gap-2">
        <button type="button" className="btn-primary" disabled={busy || !draft.name.trim()}
                onClick={() => onSave(draft)}>
          {busy ? 'Saving…' : 'Save store'}
        </button>
        <button type="button" className="btn-ghost ring-1 ring-line-strong" disabled={busy}
                onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function OrgLocations({ organizationId }) {
  const [stores, setStores] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);

  const base = `/admin/organizations/${encodeURIComponent(organizationId)}/locations`;

  const load = useCallback((signal) => apiFetch(base, { signal })
    .then((body) => setStores(body.data)), [base]);

  useEffect(() => {
    const controller = new AbortController();
    setStores(null);
    setError(null);
    load(controller.signal)
      .catch((err) => { if (err?.name !== 'AbortError') setError(consoleErrorMessage(err)); });
    return () => controller.abort();
  }, [load]);

  async function save(draft) {
    setBusy(true);
    setFormError(null);
    try {
      // `editing.id` absent means a new store. PATCH sends the whole draft rather than a
      // diff: this form holds one record's fields and all of them are the admin's, so
      // "key absent = unchanged" buys nothing and would hide a cleared field.
      await apiFetch(editing?.id ? `${base}/${editing.id}` : base, {
        method: editing?.id ? 'PATCH' : 'POST',
        body: draft,
      });
      await load();
      setEditing(null);
    } catch (err) {
      // Kept on the FORM, not at the top: the refusals that matter here name a field the
      // admin is looking at — a duplicate account number, a store that cannot be retired.
      setFormError(consoleErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-6 rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">Stores</h2>
        {stores && !editing && (
          <button type="button" className="btn-ghost ring-1 ring-line-strong"
                  onClick={() => { setEditing({ ...BLANK }); setFormError(null); }}>
            Add a store
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-ink-muted">
        The sites this organization operates. Account numbers are how a caller identifies
        their store, and every module on this platform reads these rows.
      </p>

      {error && <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>}
      {stores === null && !error && <p className="mt-2 text-sm text-ink-muted">Loading…</p>}

      {stores?.length === 0 && !editing && (
        <p className="mt-2 text-sm text-ink-muted">No stores recorded for this organization.</p>
      )}

      {stores && stores.length > 0 && (
        <ul className="mt-3 divide-y divide-line rounded-lg border border-line">
          {stores.map((store) => (
            <li key={store.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">
                  {store.name}
                  {!store.is_active && (
                    <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">retired</span>
                  )}
                </p>
                {/* The identifiers first, because "what's store key LPMK14" is how a
                    caller opens — they are what this row is looked up BY, so reading them
                    should not require opening the editor. */}
                <p className="truncate text-xs text-ink-muted">
                  {[store.account_number, store.store_number, store.store_key,
                    store.timezone, store.phone]
                    .filter(Boolean).join(' · ') || 'No account number recorded'}
                </p>
                {/* Only when there is something to say. These are filled on about 3% of
                    stores, and a row of empty labels on the other 97% would be noise
                    obscuring the line above. */}
                {(store.live_date || store.termination_date) && (
                  <p className="truncate text-xs text-ink-muted">
                    {store.live_date && `Live ${store.live_date}`}
                    {store.live_date && store.termination_date && ' · '}
                    {store.termination_date && `Terminated ${store.termination_date}`}
                  </p>
                )}
              </div>
              {/* What makes retiring an informed decision rather than a guess. The server
                  refuses while these are non-zero; showing them is how that refusal is
                  seen coming. */}
              <p className="shrink-0 text-xs text-ink-muted">
                {store.customer_count} contact{store.customer_count === 1 ? '' : 's'}
                {' · '}
                {store.ticket_count} ticket{store.ticket_count === 1 ? '' : 's'}
              </p>
              <button type="button"
                      className="shrink-0 text-xs text-ink-muted hover:text-ink"
                      onClick={() => { setEditing(store); setFormError(null); }}>
                Edit
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <StoreForm
          key={editing.id ?? 'new'}
          initial={{ ...BLANK, ...editing }}
          busy={busy}
          error={formError}
          onSave={save}
          onCancel={() => { setEditing(null); setFormError(null); }}
        />
      )}
    </section>
  );
}
